import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { getMe } from '../lib/api-client';
import { useI18n } from '../lib/i18n-context';
import { listOrderHistory } from '../lib/order-history';

/** numeric string → 加 `$` 與千分位（order history total 不帶 currency）。 */
function formatAmount(raw: string): string {
  const n = Number(raw);
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US')}` : raw;
}

/** 訂單狀態色：終止/失效態灰、異常態紅、其餘海港藍。 */
function orderStateColor(state: string): string {
  if (state === 'EXPIRED' || state === 'CANCELLED') return 'var(--boat-text-tertiary)';
  if (state === 'OVERSOLD' || state === 'PAYMENT_FAILED') return 'var(--boat-danger)';
  return 'var(--boat-primary)';
}

/**
 * 我的訂單 — `/me/orders`。
 *
 * 純 localStorage 顯示（BE 無 `GET /me/orders` endpoint；buyer 為 PII encrypted）。
 * 列出 OrderConfirm 寫入之 history（id + 寫入當下之 state + total）；
 * 不打後端取最新 state — 業務上 PAID 後 state 多半穩定，需細節走個別 order 流（無 OrderDetail 頁，暫只連 confirm）。
 *
 * TODO（業主決定 buyer-only-view 後）：補 GET /me/orders + 顯示即時 state。
 */
export function MyOrdersPage(): JSX.Element {
  const { t } = useI18n();
  const meQuery = useQuery({ queryKey: ['auth', 'me'], queryFn: getMe, retry: false });
  const history = useMemo(() => listOrderHistory(meQuery.data?.user_id), [meQuery.data?.user_id]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <h1>{t('myOrders.title')}</h1>
        <Link to="/" className="topbar-back">
          {t('common.backHome')}
        </Link>
      </header>

      {history.length === 0 ? (
        <p style={{ color: '#777' }}>{t('myOrders.empty')}</p>
      ) : (
        <>
          {history.map((o) => (
            <Link
              key={o.orderId}
              to={`/orders/${o.orderId}`}
              className="card"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 8,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'monospace' }}>
                  {o.orderId.slice(0, 8)}…
                </div>
                {o.state !== undefined && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 13,
                      fontWeight: 600,
                      color: orderStateColor(o.state),
                    }}
                  >
                    {o.state}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {o.total !== undefined && (
                  <div style={{ fontWeight: 700, fontSize: 17 }}>{formatAmount(o.total)}</div>
                )}
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--boat-text-tertiary)' }}>
                  {new Date(o.addedAt).toLocaleDateString()}
                </div>
              </div>
            </Link>
          ))}
          <p style={{ fontSize: 12, color: '#777', marginTop: 8 }}>{t('myOrders.snapshotNote')}</p>
        </>
      )}
    </div>
  );
}
